/**
 * Main payment page component
 * Handles the complete payment flow for both first-time and returning users
 */

import { useDeviceTag } from '@/hooks/useDeviceTag'
import { useMerchantCodeFromUrl } from '@/hooks/useUrlParams'
import { useMerchantData } from '@/hooks/useMerchantData'
import { useUserPaymentData } from '@/hooks/useUserPaymentData'
import { MerchantInfo } from '@/components/layout/MerchantInfo'
import { PaymentForm } from '@/components/forms/PaymentForm'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'

export function PaymentPage() {
  // Get device tag (creates one if it doesn't exist)
  const { deviceTag, error: deviceTagError, retry: retryDeviceTag, isLoading: isDeviceTagLoading } = useDeviceTag()

  // Extract merchant code from URL parameters
  const merchantCode = useMerchantCodeFromUrl()

  // Fetch merchant data
  const {
    data: merchant,
    isLoading: isMerchantLoading,
    error: merchantError,
  } = useMerchantData(merchantCode, !!merchantCode)

  // Fetch user's saved payment data (only if device tag exists)
  const { data: userPaymentData } = useUserPaymentData(deviceTag, !!deviceTag)

  // Debug: Log userPaymentData
  console.log('[PaymentPage] deviceTag:', deviceTag)
  console.log('[PaymentPage] userPaymentData:', userPaymentData)
  console.log('[PaymentPage] deviceTagError:', deviceTagError)

  // Show error if no merchant code in URL
  if (!merchantCode) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F5F6F8' }}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-error-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid QR Code</h2>
          <p className="text-gray-600">
            No merchant code found in the URL. Please scan a valid QR code.
          </p>
        </div>
      </div>
    )
  }

  // Show error if device tag registration failed
  if (deviceTagError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F5F6F8' }}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-error-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Device Registration Failed</h2>
          <p className="text-gray-600 mb-4">
            {deviceTagError.message}
          </p>
          <button
            onClick={retryDeviceTag}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
          <p className="text-sm text-gray-500 mt-4">
            Make sure to allow biometric authentication when prompted
          </p>
        </div>
      </div>
    )
  }

  // Show loading state while initial data is being fetched
  if (isMerchantLoading || isDeviceTagLoading || !deviceTag) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F6F8' }}>
        <LoadingSpinner message="Loading payment details..." size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F5F6F8' }}>
      <div className="max-w-md mx-auto">
        {/* Merchant Information */}
        <MerchantInfo merchant={merchant || null} isLoading={isMerchantLoading} error={merchantError} />

        {/* Payment Form */}
        {merchant && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <PaymentForm
              merchantCode={merchantCode}
              deviceTag={deviceTag}
              userPaymentData={userPaymentData || null}
              onSuccess={() => {
                // Could navigate to success page or show confirmation
                console.log('Payment successful')
              }}
            />
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Powered By{' '}
            <a
              href="https://www.selenite.africa"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 font-medium underline"
            >
              Selenite
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

